import "./App.css";
import {
  redirect,
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import AppLayout from "./AppLayout";
import MarketingLayout from "./Marketing/MarketingLayout";
import LeaguePublicLayout from "./LeaguePublic/LeaguePublicLayout";
import HomePage from "./LeaguePublic/HomePage";
import StandingsPage from "./LeaguePublic/Standings";
import SchedulePage from "./LeaguePublic/SchedulePage";
import StatsPage from "./LeaguePublic/StatsPage";
import GameDetailsPage from "./LeaguePublic/GameDetailsPage";
import PlayerProfilePage from "./LeaguePublic/PlayerProfilePage";
import TeamProfilePage from "./LeaguePublic/TeamProfilePage";
import LoginPage from "./Auth/LoginPage";
import RequireAuth from "./Auth/RequireAuth";
import AdminHomePage from "./Admin/AdminHomePage";
import RegisterPage from "./Auth/RegisterPage";
import PlayersPage from "./LeaguePublic/PlayersPage";
import TeamsPage from "./LeaguePublic/TeamsPage";
import AdminSeasonsPage from "./Admin/AdminSeasonsPage";
import AdminPlayersPage from "./Admin/AdminPlayersPage";
import AdminUsersPage from "./Admin/AdminUsersPage";
import AdminSeasonManagePage from "./Admin/AdminSeasonManagePage";
import AdminSeasonOverviewPage from "./Admin/AdminSeasonOverviewPage";
import AdminSeasonTeamsPage from "./Admin/AdminSeasonTeamsPage";
import AdminTeamPlayersPage from "./Admin/AdminTeamPlayersPage";
import AdminSeasonSchedulesPage from "./Admin/AdminSeasonSchedulesPage";
import AdminSeasonScoresPage from "./Admin/AdminSeasonScoresPage";
import GameScoringPage from "./Admin/GameScoringPage";
import AdminDashboard from "./Admin/AdminDashboard";
import AdminLeagueSettingsPage from "./Admin/AdminLeagueSettingsPage";
import { useAuth } from "./context/AuthContext";
import { Spinner } from "react-bootstrap";
import SetupLayout from "./Setup/SetupLayout";
import LeagueSetupPage from "./Setup/LeagueSetupPage";
import SeasonSetupPage from "./Setup/SeasonWizard/SeasonSetupPage";
import MyLeaguesPage from "./LeaguePublic/MyLeaguesPage";
import LandingPage from "./Landing/LandingPage";
import { useLeagueFromDomain } from "./hooks/useLeagueFromDomain";
import AboutPage from "./Landing/AboutPage";
import TermsPage from "./Landing/TermsPage";
import PrivacyPage from "./Landing/PrivacyPage";

function CustomDomainRedirect() {
  const domainResult = useLeagueFromDomain();

  // Show loading state while resolving domain
  if (domainResult.state === "loading") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // If we're on a custom domain and have resolved a league, redirect to league public site
  if (domainResult.state === "custom_domain_resolved" && domainResult.league) {
    return <Navigate to={`/leagues/${domainResult.league.abbr}`} replace />;
  }

  // Otherwise show the landing page (main domain or custom domain not found)
  return <LandingPage />;
}

function App() {
  let auth = useAuth();
  const router = createBrowserRouter([
    {
      id: "root",
      element: <AppLayout />,
      loader() {
        // Our root route always provides the user, if logged in
        return { user: "" };
      },
      children: [
        // ===== Marketing Site =====
        {
          element: <MarketingLayout />,
          children: [
            {
              path: "/",
              element: <CustomDomainRedirect />,
            },
            {
              path: "/login",
              element: <LoginPage />,
            },
            {
              path: "/register",
              element: <RegisterPage />,
            },
            {
              path: "/about",
              element: <AboutPage />,
            },
            {
              path: "/terms",
              element: <TermsPage />,
            },
            {
              path: "/privacy",
              element: <PrivacyPage />,
            },
          ],
        },

        // ===== Public League Site =====
        {
          path: "/leagues/:abbr",
          element: <LeaguePublicLayout />,
          children: [
            {
              index: true,
              element: <HomePage />,
            },
            {
              path: "standings",
              element: <StandingsPage />,
            },
            {
              path: "schedule",
              element: <SchedulePage />,
            },
            {
              path: "stats",
              element: <StatsPage />,
            },
            {
              path: "games/:gameId",
              element: <GameDetailsPage />,
            },
            {
              path: "players/:playerId",
              element: <PlayerProfilePage />,
            },
            {
              path: "teams/:teamId",
              element: <TeamProfilePage />,
            },
          ],
        },

        // ===== My Leagues (uses Marketing layout) =====
        {
          element: <MarketingLayout />,
          children: [
            {
              path: "/leagues",
              element: (
                <RequireAuth>
                  <MyLeaguesPage />
                </RequireAuth>
              ),
            },
          ],
        },

        // ===== Standalone Pages (No specific layout) =====
        {
          path: "/players",
          element: <PlayersPage />,
        },
        {
          path: "/teams",
          element: <TeamsPage />,
        },

        // ===== Admin Site =====
        {
          path: "/admin",
          element: (
            <RequireAuth>
              <AdminHomePage />
            </RequireAuth>
          ),
          children: [
            {
              index: true,
              element: <AdminDashboard />,
            },
            {
              path: "league-settings",
              element: <AdminLeagueSettingsPage />,
            },
            {
              path: "seasons",
              element: <AdminSeasonsPage />,
            },
            {
              path: "seasons/:seasonId",
              element: <AdminSeasonManagePage />,
              children: [
                {
                  path: "overview",
                  element: <AdminSeasonOverviewPage />,
                },
                {
                  path: "teams",
                  element: <AdminSeasonTeamsPage />,
                },
                {
                  path: "teams/:teamId/players",
                  element: <AdminTeamPlayersPage />,
                },
                {
                  path: "schedules",
                  element: <AdminSeasonSchedulesPage />,
                },
                {
                  path: "games/:gameId/score",
                  element: <GameScoringPage />,
                },
                {
                  path: "scores",
                  element: <AdminSeasonScoresPage />,
                },
              ],
            },
            {
              path: "players",
              element: <AdminPlayersPage />,
            },
            {
              path: "users",
              element: <AdminUsersPage />,
            },
          ],
        },
      ],
    },

    // ===== Logout Route (Resource Route) =====
    {
      path: "/logout",
      element: <Spinner />,
      async loader() {
        // We signout in a "resource route" that we can hit from a fetcher.Form
        const logout = async () => {
          await new Promise<void>((resolve) => {
            auth.logout(() => {
              resolve();
            });
          });
        };
        await logout();
        return redirect("/");
      },
    },

    // ===== Setup Wizard =====
    {
      path: "/setup",
      element: (
        <RequireAuth skipSetupCheck={true}>
          <SetupLayout />
        </RequireAuth>
      ),
      children: [
        {
          index: true,
          element: <LeagueSetupPage />,
        },
        {
          path: "league",
          element: <LeagueSetupPage />,
        },
        {
          path: "season",
          element: <SeasonSetupPage />,
        },
      ],
    },
  ]);

  return (
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  );

  //     {/* <Routes>
  //       <Route element={<AppLayout />}>
  //         <Route path="/" element={<HomePage />} />
  //         <Route path="/login" element={<LoginPage />} />
  //         <Route path="/standings" element={<StandingsPage />} />
  //         <Route path="/players" element={<PlayersPage />} />
  //         <Route path="/teams" element={<TeamsPage />} />
  //         <Route path="/register" element={<RegisterPage />} />
  //         <Route
  //           path="/admin"
  //           element={
  //             <RequireAuth>
  //               <AdminHomePage />
  //             </RequireAuth>
  //           }
  //         >
  //           <Route path="seasons" element={<AdminSeasonsPage />} />
  //           <Route
  //             path="seasons/:seasonId"
  //             element={<AdminSeasonManagePage />}
  //           />
  //           <Route path="players" element={<AdminPlayersPage />} />
  //         </Route>
  //         <Route
  //           path="/profile"
  //           element={
  //             <RequireAuth>
  //               <ProfilePage />
  //             </RequireAuth>
  //           }
  //         />
  //         <Route path="*" element={<p>404 NOT FOUND</p>} />
  //       </Route>
  //     </Routes> */}
  // );
}

export default App;
